import React, { useState, useEffect } from 'react';
import { FaCheckCircle, FaTimesCircle, FaEye, FaInfoCircle, FaSearch, FaPlus, FaClock, FaCalendarAlt, FaEdit, FaTrash, FaUser, FaBuilding, FaFilter, FaUserTie, FaFileAlt } from 'react-icons/fa';
import { FiAlertTriangle } from 'react-icons/fi';
import Swal from 'sweetalert2';
import useAxios from '../utils/useAxios';
import { useSidebar } from '../context/SidebarContext';
import OrganisationSelector from '../components/OrganisationSelector';
import { useAuth } from '../context/AuthContext';
import styles from '../styles/GestionAbsencesRetards';

// ─────────────────────────────────────────────────────────────
// COMPOSANT FORMULAIRES
// ─────────────────────────────────────────────────────────────
const FormFields = ({ formType, formData, setFormData }) => {
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleFileChange = (e) => {
    setFormData(prev => ({ ...prev, justificatif: e.target.files[0] }));
  };

  return (
    <>
      {formType === 'absence' && (
        <>
          <div style={styles.formCheck}>
            <input
              type="checkbox"
              style={styles.formCheckInput}
              id="absenceUnJour"
              name="absence_un_jour"
              checked={formData.absence_un_jour}
              onChange={handleInputChange}
            />
            <label style={styles.formCheckLabel} htmlFor="absenceUnJour">
              Absence sur un seul jour
            </label>
          </div>

          {formData.absence_un_jour ? (
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>Date</label>
              <input
                type="date"
                style={styles.formInput}
                name="date_unique"
                value={formData.date_unique}
                onChange={handleInputChange}
                required
              />
            </div>
          ) : (
            <>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>Date début</label>
                <input
                  type="date"
                  style={styles.formInput}
                  name="date_debut"
                  value={formData.date_debut}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>Date fin</label>
                <input
                  type="date"
                  style={styles.formInput}
                  name="date_fin"
                  value={formData.date_fin}
                  onChange={handleInputChange}
                  required
                  min={formData.date_debut}
                />
              </div>
            </>
          )}
        </>
      )}

      {formType === 'retard' && (
        <>
          <div style={styles.formGroup}>
            <label style={styles.formLabel}>Date du retard</label>
            <input
              type="date"
              style={styles.formInput}
              name="date"
              value={formData.date}
              onChange={handleInputChange}
              required
            />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.formLabel}>Heure estimée d'arrivée</label>
            <input
              type="time"
              style={styles.formInput}
              name="heure_estimee"
              value={formData.heure_estimee}
              onChange={handleInputChange}
              required
            />
          </div>
        </>
      )}

      <div style={styles.formGroup}>
        <label style={styles.formLabel}>Motif <span style={{ color: '#dc3545' }}>*</span></label>
        <textarea
          style={styles.formTextarea}
          rows="3"
          name="motif"
          value={formData.motif}
          onChange={handleInputChange}
          required
          placeholder="Décrivez le motif..."
        />
      </div>

      <div style={styles.formGroup}>
        <label style={styles.formLabel}>Justificatif <span style={{ color: '#aaa', fontWeight: '400' }}>(facultatif)</span></label>
        <input
          type="file"
          style={styles.formFile}
          name="justificatif"
          accept=".pdf,.jpg,.jpeg,.png"
          onChange={handleFileChange}
        />
      </div>
    </>
  );
};

// ─────────────────────────────────────────────────────────────
// COMPOSANT PRINCIPAL
// ─────────────────────────────────────────────────────────────
const GestionAbsencesRetards = () => {
  const api = useAxios();
  const { sidebarVisible } = useSidebar();
  const { user } = useAuth();

  // ── États ──────────────────────────────────────────────────
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedOrganisation, setSelectedOrganisation] = useState('');
  const [currentOrganisation, setCurrentOrganisation] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [pagination, setPagination] = useState({
    count: 0,
    next: null,
    previous: null,
    totalPages: 1
  });
  const [refreshKey, setRefreshKey] = useState(0);

  const [employees, setEmployees] = useState([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [selectedEmployeeId, setSelectedEmployeeId] = useState('');
  const [selectedEmployeeName, setSelectedEmployeeName] = useState('');

  const [filters, setFilters] = useState({
    date_debut: '',
    date_fin: '',
    statut: '',
    nom_employe: '',
  });

  const [stats, setStats] = useState({ total: 0, absences: 0, retards: 0, en_attente: 0 });

  // ── Modales ───────────────────────────────────────────────
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showRefusModal, setShowRefusModal] = useState(false);
  const [currentRefus, setCurrentRefus] = useState(null);
  const [motifRefus, setMotifRefus] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  const [formType, setFormType] = useState('absence');
  const [formData, setFormData] = useState({
    date_debut: '',
    date_fin: '',
    absence_un_jour: false,
    date_unique: '',
    date: '',
    heure_estimee: '',
    motif: '',
    justificatif: null,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  // ─────────────────────────────────────────────────────────
  // RENDU BADGES
  // ─────────────────────────────────────────────────────────
  const renderBadge = (statut) => {
    const map = {
      en_attente: [styles.badgeEnAttente, 'En attente'],
      'approuvée': [styles.badgeApprouvee, 'Approuvée'],
      'refusée': [styles.badgeRefusee, 'Refusée'],
    };
    const [style, label] = map[statut] || [styles.badgeInconnu, 'Inconnu'];
    return <span style={style}>{label}</span>;
  };

  const renderTypeBadge = (type) => {
    return type === 'absence'
      ? <span style={styles.badgeTypeAbsence}><FaCalendarAlt size={10} /> Absence</span>
      : <span style={styles.badgeTypeRetard}><FaClock size={10} /> Retard</span>;
  };

  const renderPeriode = (item) => {
    return item.type === 'absence'
      ? `${item.date_debut} → ${item.date_fin}`
      : `${item.date} à ${item.heure_estimee || '--:--'}`;
  };

  // ─────────────────────────────────────────────────────────
  // CHARGEMENT DES DONNÉES
  // ─────────────────────────────────────────────────────────
  const loadData = async (targetPage = 1, useUrl = null, orgId = selectedOrganisation) => {
    if (!orgId) {
      setItems([]);
      setStats({ total: 0, absences: 0, retards: 0, en_attente: 0 });
      setPagination({ count: 0, next: null, previous: null, totalPages: 1 });
      return;
    }

    setLoading(true);
    try {
      let url;
      if (useUrl) {
        url = useUrl;
      } else {
        const params = new URLSearchParams({
          page: targetPage,
          organisation: orgId,
          type: activeTab === 'all' ? '' : activeTab === 'absences' ? 'absence' : 'retard',
          page_size: pageSize === 'all' ? totalCount : pageSize
        });
        if (filters.date_debut) params.append('date_debut', filters.date_debut);
        if (filters.date_fin) params.append('date_fin', filters.date_fin);
        if (filters.statut) params.append('statut', filters.statut);
        if (filters.nom_employe) params.append('nom_employe', filters.nom_employe);
        url = `/pointage/absences-retards/?${params}`;
      }

      const res = await api.get(url);
      const results = res.data?.results || [];
      setItems(results);
      setTotalCount(res.data?.count || 0);

      if (pageSize === 'all') {
        setPagination({
          count: res.data?.count || 0,
          next: res.data?.next || null,
          previous: res.data?.previous || null,
          totalPages: 1
        });
      } else {
        setPagination({
          count: res.data?.count || 0,
          next: res.data?.next || null,
          previous: res.data?.previous || null,
          totalPages: Math.ceil((res.data?.count || 0) / pageSize) || 1
        });
      }

      if (!useUrl) {
        setPage(targetPage);
      }

      setStats({
        total: results.length,
        absences: results.filter(i => i.type === 'absence').length,
        retards: results.filter(i => i.type === 'retard').length,
        en_attente: results.filter(i => i.statut === 'en_attente').length,
      });

    } catch (error) {
      console.error('Erreur chargement:', error);
      setItems([]);
      setStats({ total: 0, absences: 0, retards: 0, en_attente: 0 });
      setPagination({ count: 0, next: null, previous: null, totalPages: 1 });
    } finally {
      setLoading(false);
    }
  };

  const getCurrentPageFromUrls = () => {
    if (pagination.previous) {
      const pageMatch = pagination.previous.match(/page=(\d+)/);
      if (pageMatch) {
        return parseInt(pageMatch[1]) + 1;
      }
    } else if (pagination.next) {
      const pageMatch = pagination.next.match(/page=(\d+)/);
      if (pageMatch) {
        return parseInt(pageMatch[1]) - 1;
      }
    }
    return 1;
  };

  const currentPage = getCurrentPageFromUrls();

  const handleNextPage = () => {
    if (pagination.next) {
      loadData(null, pagination.next);
    }
  };

  const handlePrevPage = () => {
    if (pagination.previous) {
      loadData(null, pagination.previous);
    }
  };

  // ─────────────────────────────────────────────────────────
  // EFFECTS
  // ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (selectedOrganisation) {
      loadData(1);
    }
  }, [
    selectedOrganisation,
    activeTab,
    filters.date_debut,
    filters.date_fin,
    filters.statut,
    filters.nom_employe,
    pageSize,
    refreshKey,
  ]);

  // ─── Chargement des employés ─────────────────────────────
  useEffect(() => {
    if (!selectedOrganisation) {
      setEmployees([]);
      return;
    }

    const loadEmployees = async () => {
      setEmployeesLoading(true);
      try {
        const res = await api.get(`/employes/?organisation=${selectedOrganisation}`);
        setEmployees(Array.isArray(res.data) ? res.data : []);
      } catch (error) {
        console.error('Erreur chargement employés:', error);
        setEmployees([]);
      }
      setEmployeesLoading(false);
    };

    loadEmployees();
  }, [selectedOrganisation]);

  // ─────────────────────────────────────────────────────────
  // HANDLERS
  // ─────────────────────────────────────────────────────────
  const handleOrganisationChange = (orgId, org) => {
    setSelectedOrganisation(orgId);
    setCurrentOrganisation(org);
    setPage(1);
    setSelectedEmployeeId('');
    setSelectedEmployeeName('');
    setEmployeeSearch('');
    setRefreshKey(prev => prev + 1);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    setPage(1);
  };

  const resetFilters = () => {
    setFilters({ date_debut: '', date_fin: '', statut: '', nom_employe: '' });
    setPage(1);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      loadData(newPage);
    }
  };

  const handleSelectEmployee = (emp) => {
    setSelectedEmployeeId(emp.id);
    setSelectedEmployeeName(`${emp.first_name} ${emp.last_name}`);
  };

  const clearSelectedEmployee = () => {
    setSelectedEmployeeId('');
    setSelectedEmployeeName('');
    setEmployeeSearch('');
  };

  // ─────────────────────────────────────────────────────────
  // ACTIONS
  // ─────────────────────────────────────────────────────────
  const refreshData = () => {
    setRefreshKey(prev => prev + 1);
  };

  const handleApprouver = async (id, type) => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      const endpoint = type === 'absence'
        ? `/pointage/absences/${id}/`
        : `/pointage/retards/${id}/`;
      await api.patch(endpoint, { statut: 'approuvée' });
      refreshData();
      Swal.fire('Succès', `${type === 'absence' ? 'Absence' : 'Retard'} approuvé.`, 'success');
    } catch (error) {
      console.error('Erreur approbation:', error);
      Swal.fire('Erreur', 'Impossible de valider', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRefuserClick = (item) => {
    setCurrentRefus(item);
    setMotifRefus('');
    setShowRefusModal(true);
  };

  const handleRefusSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    if (!motifRefus.trim()) {
      Swal.fire('Erreur', 'Veuillez indiquer un motif de refus.', 'error');
      return;
    }
    setIsSubmitting(true);
    try {
      const endpoint = currentRefus.type === 'absence'
        ? `/pointage/absences/${currentRefus.id}/`
        : `/pointage/retards/${currentRefus.id}/`;
      await api.patch(endpoint, { statut: 'refusée', motif_refus: motifRefus.trim() });
      setShowRefusModal(false);
      refreshData();
      Swal.fire('Succès', 'Demande refusée.', 'success');
    } catch (error) {
      console.error('Erreur refus:', error);
      Swal.fire('Erreur', 'Impossible de refuser', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditClick = (item) => {
    setEditingItem(item);
    setFormType(item.type);
    setFormData({
      date_debut: item.date_debut || '',
      date_fin: item.date_fin || '',
      date: item.date || '',
      heure_estimee: item.heure_estimee || '',
      motif: item.motif || '',
      absence_un_jour: item.date_debut === item.date_fin,
      date_unique: item.date_debut || '',
      justificatif: null,
    });
    setShowEditModal(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      const formPayload = new FormData();
      if (formType === 'absence') {
        const dd = formData.absence_un_jour ? formData.date_unique : formData.date_debut;
        const df = formData.absence_un_jour ? formData.date_unique : formData.date_fin;
        formPayload.append('date_debut', dd);
        formPayload.append('date_fin', df);
      } else {
        formPayload.append('date', formData.date);
        formPayload.append('heure_estimee_arrivee', formData.heure_estimee);
      }
      formPayload.append('motif', formData.motif);
      if (formData.justificatif) formPayload.append('justificatif', formData.justificatif);

      const endpoint = formType === 'absence'
        ? `/pointage/absences/${editingItem.id}/`
        : `/pointage/retards/${editingItem.id}/`;
      await api.patch(endpoint, formPayload, { headers: { 'Content-Type': 'multipart/form-data' } });
      setShowEditModal(false);
      refreshData();
      Swal.fire('Succès', 'Demande modifiée.', 'success');
    } catch (error) {
      console.error('Erreur modification:', error);
      Swal.fire('Erreur', 'Impossible de modifier', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id, type) => {
    if (isSubmitting) return;
    const result = await Swal.fire({
      title: 'Confirmer la suppression',
      text: 'Cette action est irréversible.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      confirmButtonText: 'Supprimer',
      cancelButtonText: 'Annuler',
    });
    if (result.isConfirmed) {
      setIsSubmitting(true);
      try {
        const endpoint = type === 'absence'
          ? `/pointage/absences/${id}/`
          : `/pointage/retards/${id}/`;
        await api.delete(endpoint);
        refreshData();
        Swal.fire('Succès', 'Demande supprimée.', 'success');
      } catch (error) {
        console.error('Erreur suppression:', error);
        Swal.fire('Erreur', 'Impossible de supprimer', 'error');
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const resetCreateForm = () => {
    setFormData({
      date_debut: '',
      date_fin: '',
      absence_un_jour: false,
      date_unique: '',
      date: '',
      heure_estimee: '',
      motif: '',
      justificatif: null,
    });
    setSelectedEmployeeId('');
    setSelectedEmployeeName('');
    setEmployeeSearch('');
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) return;

    if (!selectedEmployeeId) {
      Swal.fire('Erreur', 'Veuillez sélectionner un employé.', 'error');
      return;
    }

    setIsSubmitting(true);

    try {
      const formPayload = new FormData();
      formPayload.append('employe', selectedEmployeeId);
      formPayload.append('organisation', selectedOrganisation);
      formPayload.append('motif', formData.motif);

      if (formType === 'absence') {
        const dd = formData.absence_un_jour ? formData.date_unique : formData.date_debut;
        const df = formData.absence_un_jour ? formData.date_unique : formData.date_fin;
        if (!dd || !df) {
          Swal.fire('Erreur', 'Veuillez renseigner les dates.', 'error');
          setIsSubmitting(false);
          return;
        }
        formPayload.append('date_debut', dd);
        formPayload.append('date_fin', df);
      } else {
        if (!formData.date || !formData.heure_estimee) {
          Swal.fire('Erreur', 'Veuillez renseigner la date et l\'heure.', 'error');
          setIsSubmitting(false);
          return;
        }
        formPayload.append('date', formData.date);
        formPayload.append('heure_estimee_arrivee', formData.heure_estimee);
      }

      if (formData.justificatif) formPayload.append('justificatif', formData.justificatif);

      const endpoint = formType === 'absence'
        ? '/pointage/absences/rh/creer/'
        : '/pointage/retards/rh/creer/';

      await api.post(endpoint, formPayload, { headers: { 'Content-Type': 'multipart/form-data' } });

      setShowCreateModal(false);
      resetCreateForm();
      setPage(1);
      refreshData();
      Swal.fire({
        icon: 'success',
        title: 'Demande créée avec succès',
        text: `La ${formType === 'absence' ? "demande d'absence" : 'demande de retard'} a été créée et automatiquement approuvée.`,
        confirmButtonColor: '#1B019B'
      });
    } catch (error) {
      const msg = error?.response?.data?.detail || 'Impossible de créer la demande';
      Swal.fire('Erreur', msg, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const generatePageSizeOptions = () => {
    const options = [10, 20, 30, 50];

    if (totalCount > 50 && !options.includes(totalCount)) {
      options.push(totalCount);
    }

    options.push('all');
    return options;
  };

  // ─────────────────────────────────────────────────────────
  // PERMISSIONS
  // ─────────────────────────────────────────────────────────
  const canCreate = user?.is_super_admin || user?.permissions?.includes('CREATE_LEAVE_REQUEST') || user?.permissions?.includes('CREATE_LATE_SIGNAL');
  const canApprove = user?.is_super_admin || user?.permissions?.includes('APPROVE_LEAVE_REQUEST') || user?.permissions?.includes('APPROVE_LATE_SIGNAL');
  const canReject = user?.is_super_admin || user?.permissions?.includes('REJECT_LEAVE_REQUEST');
  const canEdit = user?.is_super_admin;
  const canDelete = user?.is_super_admin;

  // ─────────────────────────────────────────────────────────
  // FILTRAGE DES EMPLOYÉS
  // ─────────────────────────────────────────────────────────
  const filteredEmployees = employees.filter(emp => {
    const fullName = `${emp.first_name} ${emp.last_name}`.toLowerCase();
    return fullName.includes(employeeSearch.toLowerCase());
  });

  // ─────────────────────────────────────────────────────────
  // RENDU PRINCIPAL
  // ─────────────────────────────────────────────────────────
  return (
    <div style={{ ...styles.container, marginLeft: sidebarVisible ? 250 : 0 }}>

      {/* ── MODALE DÉTAIL ── */}
      {showDetailModal && selectedItem && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" style={{ color: '#1B019B' }}>
                  Détail de la demande
                </h5>
                <button className="btn-close" onClick={() => setShowDetailModal(false)}></button>
              </div>
              <div className="modal-body">
                <div style={styles.detailGrid}>
                  <div style={styles.detailItem}><strong>Type :</strong> {renderTypeBadge(selectedItem.type)}</div>
                  <div style={styles.detailItem}><strong>Employé :</strong> {selectedItem.employe_nom}</div>
                  <div style={styles.detailItem}><strong>Période :</strong> {renderPeriode(selectedItem)}</div>
                  <div style={styles.detailItem}><strong>Statut :</strong> {renderBadge(selectedItem.statut)}</div>
                  <div style={styles.detailItem}>
                    <strong>Motif :</strong>
                    <div style={styles.justificationText}>{selectedItem.motif || '—'}</div>
                  </div>
                  {selectedItem.justificatif && (
                    <div style={styles.detailItem}>
                      <strong>Fichier joint :</strong><br />
                      <a href={selectedItem.justificatif} target="_blank" rel="noopener noreferrer" style={styles.fileLink}>
                        <FaEye /> Voir le fichier
                      </a>
                    </div>
                  )}
                  {selectedItem.motif_refus && (
                    <div style={styles.detailItem}>
                      <strong>Motif de refus :</strong>
                      <div style={{ ...styles.justificationText, color: '#dc3545' }}>{selectedItem.motif_refus}</div>
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowDetailModal(false)}>Fermer</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── MODALE REFUS ── */}
      {showRefusModal && currentRefus && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" style={{ color: '#dc3545' }}>
                  Refuser la demande
                </h5>
                <button className="btn-close" onClick={() => setShowRefusModal(false)}></button>
              </div>
              <form onSubmit={handleRefusSubmit}>
                <div className="modal-body">
                  <div style={{ marginBottom: '12px', fontSize: '0.9rem', color: '#555' }}>
                    <FaUser style={{ marginRight: 6 }} />
                    <strong>{currentRefus.employe_nom}</strong> — {renderTypeBadge(currentRefus.type)}
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.formLabel}>
                      Motif de refus <span style={{ color: '#dc3545' }}>*</span>
                    </label>
                    <textarea
                      style={styles.formTextarea}
                      rows="4"
                      value={motifRefus}
                      onChange={e => setMotifRefus(e.target.value)}
                      placeholder="Saisir le motif de refus..."
                      required
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowRefusModal(false)}>Annuler</button>
                  <button
                    type="submit"
                    style={{ ...styles.submitButton, backgroundColor: '#dc3545' }}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'En cours...' : 'Refuser'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* ── MODALE CRÉATION ── */}
      {showCreateModal && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" style={{ color: '#1B019B' }}>
                  <FaPlus style={{ marginRight: 6 }} /> Nouvelle demande
                </h5>
                <button className="btn-close" onClick={() => { setShowCreateModal(false); resetCreateForm(); setIsSubmitting(false); }}></button>
              </div>
              <form onSubmit={handleCreateSubmit}>
                <div className="modal-body">
                  {/* Sélecteur employé */}
                  <div style={styles.employeeSelectorBox}>
                    <div style={styles.employeeSelectorTitle}>
                      <FaUserTie /> Sélectionner l'employé <span style={{ color: '#dc3545' }}>*</span>
                    </div>

                    {selectedEmployeeId ? (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span style={styles.selectedEmployeeBadge}>
                          <FaUser size={12} /> {selectedEmployeeName}
                        </span>
                        <button
                          type="button"
                          style={{ ...styles.cancelButton, padding: '4px 12px', fontSize: '0.8rem' }}
                          onClick={clearSelectedEmployee}
                          disabled={isSubmitting}
                        >
                          Changer
                        </button>
                      </div>
                    ) : (
                      <>
                        <div style={{ position: 'relative' }}>
                          <input
                            type="text"
                            placeholder="Rechercher un employé..."
                            value={employeeSearch}
                            onChange={e => setEmployeeSearch(e.target.value)}
                            style={styles.employeeSearchInput}
                            disabled={isSubmitting}
                          />
                          <FaSearch style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', color: '#aaa' }} />
                        </div>

                        {employeesLoading ? (
                          <div style={{ padding: '10px', textAlign: 'center', color: '#999', fontSize: '0.85rem' }}>
                            Chargement...
                          </div>
                        ) : filteredEmployees.length === 0 ? (
                          <div style={{ padding: '10px', textAlign: 'center', color: '#bbb', fontSize: '0.85rem' }}>
                            {employees.length === 0
                              ? 'Aucun employé dans cette organisation.'
                              : 'Aucun résultat pour cette recherche.'}
                          </div>
                        ) : (
                          <div style={styles.employeeList}>
                            {filteredEmployees.map(emp => (
                              <div
                                key={emp.id}
                                style={{
                                  ...styles.employeeListItem,
                                  ...(selectedEmployeeId === emp.id ? styles.employeeListItemActive : {}),
                                  opacity: isSubmitting ? 0.5 : 1,
                                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                                }}
                                onClick={() => !isSubmitting && handleSelectEmployee(emp)}
                              >
                                <FaUser size={12} style={{ color: '#1B019B', flexShrink: 0 }} />
                                <div>
                                  <div style={{ fontWeight: '500' }}>{emp.first_name} {emp.last_name}</div>
                                  {emp.poste && <div style={{ fontSize: '0.78rem', color: '#888' }}>{emp.poste}</div>}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  {/* Sélecteur type */}
                  <div style={styles.typeSelector}>
                    <div
                      style={{
                        ...styles.typeOption,
                        ...(formType === 'absence' ? styles.typeOptionActive : {}),
                        opacity: isSubmitting ? 0.5 : 1,
                        cursor: isSubmitting ? 'not-allowed' : 'pointer',
                      }}
                      onClick={() => !isSubmitting && setFormType('absence')}
                    >
                      <FaCalendarAlt /> Absence
                    </div>
                    <div
                      style={{
                        ...styles.typeOption,
                        ...(formType === 'retard' ? styles.typeOptionActive : {}),
                        opacity: isSubmitting ? 0.5 : 1,
                        cursor: isSubmitting ? 'not-allowed' : 'pointer',
                      }}
                      onClick={() => !isSubmitting && setFormType('retard')}
                    >
                      <FaClock /> Retard
                    </div>
                  </div>

                  <FormFields
                    formType={formType}
                    formData={formData}
                    setFormData={setFormData}
                  />
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => { setShowCreateModal(false); resetCreateForm(); setIsSubmitting(false); }}
                    disabled={isSubmitting}
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    style={styles.submitButton}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" style={{ marginRight: '8px' }}></span>
                        Enregistrement...
                      </>
                    ) : (
                      <>
                        <FaPlus /> Enregistrer
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* ── MODALE ÉDITION ── */}
      {showEditModal && editingItem && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" style={{ color: '#1B019B' }}>
                  <FaEdit style={{ marginRight: 6 }} /> Modifier la demande
                </h5>
                <button className="btn-close" onClick={() => { setShowEditModal(false); setIsSubmitting(false); }}></button>
              </div>
              <form onSubmit={handleEditSubmit}>
                <div className="modal-body">
                  <div style={{ marginBottom: '15px', padding: '10px 14px', backgroundColor: '#f8f6ff', borderRadius: '8px', fontSize: '0.9rem' }}>
                    <FaUser style={{ marginRight: 6, color: '#1B019B' }} />
                    <strong>{editingItem.employe_nom}</strong> — {renderTypeBadge(editingItem.type)}
                  </div>

                  <FormFields
                    formType={formType}
                    formData={formData}
                    setFormData={setFormData}
                  />
                  {editingItem.justificatif && (
                    <div style={{ fontSize: '0.82rem', color: '#888', marginTop: '-10px', marginBottom: '15px' }}>
                      <FaFileAlt style={{ marginRight: 4 }} />
                      Fichier actuel : {editingItem.justificatif.split('/').pop()}
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => { setShowEditModal(false); setIsSubmitting(false); }}
                    disabled={isSubmitting}
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    style={styles.submitButton}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" style={{ marginRight: '8px' }}></span>
                        Enregistrement...
                      </>
                    ) : (
                      'Enregistrer'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* ── ENTÊTE ── */}
      <div className="text-center mb-3">
        <h3 style={styles.header}>Gestion des Absences et Retards</h3>
        <hr style={styles.hr} />
      </div>

      {/* ── ORGANISATION ── */}
      <div style={styles.organisationHeader}>
        <OrganisationSelector
          onOrganisationChange={handleOrganisationChange}
          selectedOrganisation={selectedOrganisation}
        />
        <div style={styles.actionButtonsContainer}>
          {canCreate && (
            <>
              <button
                style={{ ...styles.createButton, ...styles.createAbsenceButton, opacity: !selectedOrganisation ? 0.55 : 1 }}
                onClick={() => { setFormType('absence'); resetCreateForm(); setShowCreateModal(true); setIsSubmitting(false); }}
                disabled={!selectedOrganisation || isSubmitting}
              >
                <FaCalendarAlt /> Enregistrer une absence
              </button>
              <button
                style={{ ...styles.createButton, ...styles.createRetardButton, opacity: !selectedOrganisation ? 0.55 : 1 }}
                onClick={() => { setFormType('retard'); resetCreateForm(); setShowCreateModal(true); setIsSubmitting(false); }}
                disabled={!selectedOrganisation || isSubmitting}
              >
                <FaClock /> Signaler un retard
              </button>
            </>
          )}
        </div>
      </div>

      {currentOrganisation ? (
        <>
          {/* ── FILTRES ── */}
          <div style={styles.filterContainer}>
            <div style={styles.filterGroup}>
              <span style={styles.filterLabel}><FaCalendarAlt /> Début</span>
              <input type="date" name="date_debut" value={filters.date_debut}
                onChange={handleFilterChange} style={styles.dateInput} />
            </div>
            <div style={styles.filterGroup}>
              <span style={styles.filterLabel}><FaCalendarAlt /> Fin</span>
              <input type="date" name="date_fin" value={filters.date_fin}
                onChange={handleFilterChange} style={styles.dateInput} />
            </div>
            <div style={styles.filterGroup}>
              <select name="statut" value={filters.statut} onChange={handleFilterChange} style={styles.select}>
                <option value="">Tous les statuts</option>
                <option value="en_attente">En attente</option>
                <option value="approuvée">Approuvée</option>
                <option value="refusée">Refusée</option>
              </select>
            </div>
            <div style={styles.filterGroup}>
              <div style={styles.searchWrapper}>
                <input type="text" name="nom_employe" value={filters.nom_employe}
                  onChange={handleFilterChange} style={styles.searchInput} placeholder="Rechercher par nom" />
                <FaSearch style={styles.searchIcon} />
              </div>
            </div>
            <div style={styles.filterGroup}>
              <label style={styles.pageSizeLabel}>Afficher :</label>
              <select
                style={styles.select}
                value={pageSize}
                onChange={(e) => {
                  setPageSize(e.target.value === 'all' ? 'all' : parseInt(e.target.value));
                }}
                disabled={loading}
              >
                {generatePageSizeOptions().map((size, idx) => (
                  <option key={idx} value={size}>
                    {size === 'all' ? 'Tous' : size}
                  </option>
                ))}
              </select>
            </div>
            {/* <button style={styles.resetButton} onClick={resetFilters}>
              <FaFilter /> Réinitialiser
            </button> */}
          </div>

          {/* ── ONGLETS ── */}
          <div style={styles.tabsContainer}>
            {[
              { key: 'all', label: '📋 Toutes', count: stats.total },
              { key: 'absences', label: null, icon: <FaCalendarAlt />, text: 'Absences', count: stats.absences },
              { key: 'retards', label: null, icon: <FaClock />, text: 'Retards', count: stats.retards },
            ].map(tab => (
              <button
                key={tab.key}
                style={{ ...styles.tab, ...(activeTab === tab.key ? styles.tabActive : {}) }}
                onClick={() => { setActiveTab(tab.key); setPage(1); }}
              >
                {tab.label || <>{tab.icon} {tab.text}</>}
                <span style={{ ...styles.tabBadge, ...(activeTab === tab.key ? styles.tabBadgeActive : {}) }}>
                  {tab.count}
                </span>
              </button>
            ))}
            <div style={{ marginLeft: 'auto', ...styles.infoText }}>
              <FaInfoCircle /> En attente : <strong>{stats.en_attente}</strong>
            </div>
          </div>

          {/* ── TABLEAU ── */}
          <div className="card shadow-sm" style={styles.card}>
            <div className="card-body table-responsive p-0">
              <table className="table table-hover text-center align-middle m-0">
                <thead>
                  <tr style={styles.tableHeader}>
                    <th>Type</th>
                    <th>Employé</th>
                    <th>Période</th>
                    <th>Détails</th>
                    <th>Statut</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="6" className="text-muted py-4">
                        <div className="d-flex justify-content-center align-items-center">
                          <div className="spinner-border text-primary me-2" role="status">
                            <span className="visually-hidden">Chargement...</span>
                          </div>
                          <span>Chargement des données...</span>
                        </div>
                      </td>
                    </tr>
                  ) : items.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="text-muted py-4">
                        <FaFileAlt size={40} style={{ color: '#ddd', marginBottom: 10 }} />
                        <div>Aucune demande trouvée.</div>
                      </td>
                    </tr>
                  ) : items.map(item => (
                    <tr key={`${item.type}-${item.id}`}>
                      <td>{renderTypeBadge(item.type)}</td>
                      <td>{item.employe_nom}</td>
                      <td style={{ fontSize: '0.88rem' }}>{renderPeriode(item)}</td>
                      <td>
                        <button style={styles.detailButton} onClick={() => { setSelectedItem(item); setShowDetailModal(true); }}>
                          <FaInfoCircle /> Détail
                        </button>
                      </td>
                      <td>{renderBadge(item.statut)}</td>
                      <td>
                        <div style={styles.actionButtons}>
                          {item.statut === 'en_attente' && (
                            <>
                              {canApprove && (
                                <button
                                  style={{ ...styles.actionButton, ...styles.approveButton }}
                                  onClick={() => handleApprouver(item.id, item.type)}
                                  title="Approuver"
                                  disabled={loading || isSubmitting}
                                >
                                  <FaCheckCircle />
                                </button>
                              )}
                              {canReject && (
                                <button
                                  style={{ ...styles.actionButton, ...styles.refuseButton }}
                                  onClick={() => handleRefuserClick(item)}
                                  title="Refuser"
                                  disabled={loading || isSubmitting}
                                >
                                  <FaTimesCircle />
                                </button>
                              )}
                            </>
                          )}
                          {canEdit && item.statut === 'en_attente' && (
                            <button
                              style={{ ...styles.actionButton, ...styles.editButton }}
                              onClick={() => handleEditClick(item)}
                              title="Modifier"
                              disabled={loading || isSubmitting}
                            >
                              <FaEdit />
                            </button>
                          )}
                          {canDelete && (
                            <button
                              style={{ ...styles.actionButton, ...styles.deleteButton }}
                              onClick={() => handleDelete(item.id, item.type)}
                              title="Supprimer"
                              disabled={loading || isSubmitting}
                            >
                              <FaTrash />
                            </button>
                          )}
                          {item.statut !== 'en_attente' && !canEdit && (
                            <span style={{ fontSize: '0.8rem', color: '#aaa' }}>Aucune action</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* ── PAGINATION ── */}
            {pagination.totalPages > 1 && (
              <div className="card-footer d-flex justify-content-center mt-3 bg-transparent border-top-0">
                <nav>
                  <ul className="pagination mb-0">
                    <li className={`page-item ${!pagination.previous ? 'disabled' : ''}`}>
                      <button
                        className="page-link"
                        onClick={handlePrevPage}
                        disabled={!pagination.previous || loading}
                        style={!pagination.previous ? styles.paginationDisabled : styles.paginationButton}
                      >
                        &laquo; Précédent
                      </button>
                    </li>

                    {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                      let pageNum;
                      if (pagination.totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= pagination.totalPages - 2) {
                        pageNum = pagination.totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }

                      return (
                        <li key={pageNum} className={`page-item ${currentPage === pageNum ? 'active' : ''}`}>
                          <button
                            className="page-link"
                            onClick={() => handlePageChange(pageNum)}
                            disabled={loading}
                            style={currentPage === pageNum ? { ...styles.paginationButton, ...styles.paginationActive } : styles.paginationButton}
                          >
                            {pageNum}
                          </button>
                        </li>
                      );
                    })}

                    <li className={`page-item ${!pagination.next ? 'disabled' : ''}`}>
                      <button
                        className="page-link"
                        onClick={handleNextPage}
                        disabled={!pagination.next || loading}
                        style={!pagination.next ? styles.paginationDisabled : styles.paginationButton}
                      >
                        Suivant &raquo;
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            )}
          </div>
        </>
      ) : (
        <div className="text-center py-5">
          <p className="text-muted">Sélectionnez une organisation pour commencer</p>
        </div>
      )}
    </div>
  );
};

export default GestionAbsencesRetards;